import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useNotifications } from '../../contexts/NotificationContext'
import './Notifications.css'

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1'

export default function Notifications({ onBack, onNotificationUpdate }) {
  const { user, session } = useAuth()
  const { acceptChallenge, declineChallenge, acceptFriendRequest, declineFriendRequest } = useNotifications()
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Push a new state to the history when the component mounts
    window.history.pushState({ page: 'notifications' }, '', '#notifications')

    // Handle browser back button
    const handlePopState = () => {
      onBack()
    }

    window.addEventListener('popstate', handlePopState)

    // Cleanup on unmount
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [onBack])

  useEffect(() => {
    loadNotifications()
    loadUnreadCount()
  }, [])

  const loadNotifications = async () => {
    if (!user || !session?.access_token) return

    setLoading(true)
    setError(null)

    try {
      const token = session.access_token
      const response = await fetch(`${API_BASE}/notifications`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch notifications')
      }

      const data = await response.json()
      setNotifications(data.notifications || [])
    } catch (err) {
      console.error('Error loading notifications:', err)
      setError('Failed to load notifications')
    } finally {
      setLoading(false)
    }
  }

  const loadUnreadCount = async () => {
    if (!user || !session?.access_token) return

    try {
      const token = session.access_token
      const response = await fetch(`${API_BASE}/notifications/count`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch unread count')
      }

      const data = await response.json()
      setUnreadCount(data.unread_count || 0)
    } catch (err) {
      console.error('Error loading unread count:', err)
    }
  }

  const markAsRead = async (notificationId) => {
    if (!user || !session?.access_token) return

    try {
      const token = session.access_token
      const response = await fetch(`${API_BASE}/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to mark as read')
      }

      // Update local state
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId
            ? { ...notif, read: true }
            : notif
        )
      )

      // Update unread count
      loadUnreadCount()

      // Update dashboard notification count
      if (onNotificationUpdate) {
        onNotificationUpdate()
      }
    } catch (err) {
      console.error('Error marking notification as read:', err)
    }
  }

  const markAllAsRead = async () => {
    if (!user || !session?.access_token) return

    try {
      const token = session.access_token
      const response = await fetch(`${API_BASE}/notifications/read-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to mark all as read')
      }

      // Update local state
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, read: true }))
      )

      setUnreadCount(0)

      // Update dashboard notification count
      if (onNotificationUpdate) {
        onNotificationUpdate()
      }
    } catch (err) {
      console.error('Error marking all notifications as read:', err)
    }
  }

  const deleteNotification = async (notificationId) => {
    if (!user || !session?.access_token) return

    try {
      const token = session.access_token
      const response = await fetch(`${API_BASE}/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to delete notification')
      }

      // Remove from local state
      setNotifications(prev =>
        prev.filter(notif => notif.id !== notificationId)
      )

      // Update unread count
      loadUnreadCount()
    } catch (err) {
      console.error('Error deleting notification:', err)
    }
  }

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'friend_request':
        return '👥'
      case 'friend_accepted':
        return '✅'
      case 'game_invite':
        return '⚔️'
      case 'game_result':
        return '🏆'
      case 'challenge':
        return '⚔️'
      case 'tournament':
        return '🏆'
      default:
        return '🔔'
    }
  }

  const formatTimeAgo = (timestamp) => {
    const now = new Date()
    const notifDate = new Date(timestamp)
    const diffMs = now - notifDate
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays}d ago`

    return notifDate.toLocaleDateString()
  }

  return (
    <div className="notifications-container">
      <div className="notifications-header">
        <h1>Notifications</h1>
        <div className="notifications-actions">
          {unreadCount > 0 && (
            <button className="mark-all-read-btn" onClick={markAllAsRead}>
              Mark all as read ({unreadCount})
            </button>
          )}
        </div>
      </div>

      <div className="notifications-content">
        {error && (
          <div className="error-message">{error}</div>
        )}

        {loading && (
          <div className="loading">Loading notifications...</div>
        )}

        {!loading && !error && (
          <>
            {notifications.length === 0 ? (
              <div className="empty-state">
                <p>No notifications yet</p>
                <p>You'll see friend requests, game invites, and other updates here.</p>
              </div>
            ) : (
              <div className="notifications-list">
                {notifications.map(notification => (
                  <div
                    key={notification.id}
                    className={`notification-content ${notification.read ? 'read' : 'unread'}`}
                  >
                    <div className={`notification-indicator ${notification.read ? 'read' : 'unread'}`}></div>
                    <div className="notification-main">
                      <div className="notification-text">
                        <div className="notification-title">{notification.title}</div>
                        <div className="notification-message">{notification.message}</div>
                        <div className="notification-time">{formatTimeAgo(notification.created_at)}</div>
                      </div>
                    </div>
                    <button
                      className="mark-read-btn"
                      onClick={() => markAsRead(notification.id)}
                      title="Mark as read"
                    >
                      Mark as read
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}